import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { thresholdsFromSettings, makeScostBand, DEFAULT_SCOST_THRESHOLDS } from "@/lib/scostBands";

// Global admin-configurable settings (upload cap, default city, scostamento
// bands, tier quotas). Fetched once after login from GET /admin/settings —
// the endpoint is readable by any authenticated user so non-admins can still
// consume the values (e.g. the property dialog needs the upload cap).
const SettingsContext = createContext(null);

const FALLBACK = {
  max_attachment_mb: 8,
  default_citta: "ROMA",
  scost_band_red: -30.0,
  scost_band_yellow: -20.0,
  scost_band_green: -10.0,
  scost_band_cyan: 0.0,
  tier_limits: {
    demo:   { max_properties: 10,   max_boards: 5 },
    rookie: { max_properties: 500,  max_boards: 10 },
    pro:    { max_properties: -1,   max_boards: -1 },
  },
};

export const SettingsProvider = ({ children }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(FALLBACK);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/admin/settings");
      setSettings({ ...FALLBACK, ...r.data });
      setLoaded(true);
    } catch {
      // Silently keep fallback — the app must remain usable even if the
      // settings endpoint is temporarily unavailable.
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  const scostThresholds = useMemo(() => thresholdsFromSettings(settings), [settings]);
  const scostBand = useMemo(() => makeScostBand(scostThresholds), [scostThresholds]);
  const maxAttachBytes = useMemo(
    () => Math.max(1, Math.min(32, settings.max_attachment_mb || 8)) * 1024 * 1024,
    [settings.max_attachment_mb]
  );

  const value = { settings, loaded, refresh, scostThresholds, scostBand, maxAttachBytes };
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    // Safe fallback for components rendered outside the provider (e.g. tests).
    return {
      settings: FALLBACK,
      loaded: false,
      refresh: () => {},
      scostThresholds: DEFAULT_SCOST_THRESHOLDS,
      scostBand: makeScostBand(DEFAULT_SCOST_THRESHOLDS),
      maxAttachBytes: 8 * 1024 * 1024,
    };
  }
  return ctx;
};
